import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Heart, MessageCircle, Share2, Filter } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const mockFeedItems = [
  {
    id: 1,
    company: "Tesla",
    type: "news",
    timestamp: "1 hour ago",
    title: "Q4 2024 Earnings Beat Expectations",
    excerpt: "Tesla reported record quarterly revenue and $3.1B net income, exceeding analyst expectations.",
    content: "Tesla exceeded earnings expectations with strong Q4 performance driven by vehicle deliveries and energy products.",
    tags: ["Tesla", "Earnings", "Financial"],
    image: "📊",
    likes: 2340,
    comments: 456,
    liked: false,
  },
  {
    id: 2,
    company: "SpaceX",
    type: "launch",
    timestamp: "3 hours ago",
    title: "Falcon 9 Starlink Flight Scheduled",
    excerpt: "SpaceX to launch 60 new Starlink satellites from Kennedy Space Center.",
    content: "Next Falcon 9 launch window: 2:30 PM EST. Launch will add 60 satellites to constellation.",
    tags: ["SpaceX", "Starlink", "Launch"],
    image: "🚀",
    likes: 5620,
    comments: 890,
    liked: false,
  },
  {
    id: 3,
    company: "Neuralink",
    type: "announcement",
    timestamp: "5 hours ago",
    title: "Patient 2 Achieves Motor Control Milestone",
    excerpt: "Second Neuralink patient demonstrates improved motor control and independence.",
    content: "Patient demonstrates significant progress in movement control and daily living activities.",
    tags: ["Neuralink", "Medical", "Innovation"],
    image: "🧠",
    likes: 4120,
    comments: 678,
    liked: false,
  },
  {
    id: 4,
    company: "Starlink",
    type: "update",
    timestamp: "6 hours ago",
    title: "Global Coverage Now 98% of Earth",
    excerpt: "Starlink satellite internet now available in 98% of Earth's surface.",
    content: "Expansion continues with 1M+ active subscribers worldwide and growing.",
    tags: ["Starlink", "Coverage", "Global"],
    image: "🛰️",
    likes: 3890,
    comments: 567,
    liked: false,
  },
  {
    id: 5,
    company: "Tesla",
    type: "product",
    timestamp: "8 hours ago",
    title: "Cybertruck Production Ramping",
    excerpt: "Tesla increases Cybertruck weekly production ahead of 2025 targets.",
    content: "Production now reaching 5,000 units per week with improvements in manufacturing efficiency.",
    tags: ["Tesla", "Cybertruck", "Production"],
    image: "⚡",
    likes: 2960,
    comments: 445,
    liked: false,
  },
];

const companyFilters = [
  "All",
  "Tesla",
  "SpaceX",
  "Starlink",
  "Neuralink",
  "The Boring Company",
];

const typeFilters = ["All", "News", "Launch", "Announcement", "Update", "Product"];

export default function Feed() {
  const [items, setItems] = useState(mockFeedItems);
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const toggleLike = (id: number) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, liked: !item.liked, likes: item.liked ? item.likes - 1 : item.likes + 1 } : item
    ));
  };

  const filteredItems = items.filter(item => {
    const companyMatch = selectedCompany === "All" || item.company === selectedCompany;
    const typeMatch = selectedType === "All" || item.type === selectedType.toLowerCase();
    const searchMatch = searchQuery === "" || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return companyMatch && typeMatch && searchMatch;
  });

  return (
    <div className="space-y-8">
      {/* Search & Filters */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="relative">
          <Input
            placeholder="Search updates, launches, announcements..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-card border-border pl-4"
          />
        </div>

        {/* Company Filter */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Companies</p>
          <div className="flex flex-wrap gap-2">
            {companyFilters.map((company) => (
              <motion.button
                key={company}
                whileHover={{ scale: 1.05 }}
                onClick={() => setSelectedCompany(company)}
                className={`px-3 py-2 text-xs font-medium rounded transition-all ${
                  selectedCompany === company
                    ? "bg-foreground text-background"
                    : "bg-card border border-border hover:border-foreground"
                }`}
              >
                {company}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Type Filter */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Type</p>
          <div className="flex flex-wrap gap-2">
            {typeFilters.map((type) => (
              <motion.button
                key={type}
                whileHover={{ scale: 1.05 }}
                onClick={() => setSelectedType(type)}
                className={`px-3 py-2 text-xs font-medium rounded transition-all ${
                  selectedType === type
                    ? "bg-foreground text-background"
                    : "bg-card border border-border hover:border-foreground"
                }`}
              >
                {type}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Results */}
      <motion.div
        className="text-xs text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        Showing {filteredItems.length} {filteredItems.length === 1 ? "update" : "updates"}
      </motion.div>

      {/* Feed Items */}
      <motion.div
        className="space-y-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              whileHover={{ y: -2 }}
              className="border border-border p-6 hover:border-foreground/50 transition-colors group"
            >
              {/* Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className="text-4xl">{item.image}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {item.company}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </Badge>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {item.timestamp}
                    </span>
                  </div>
                  <h2 className="text-xl font-medium text-foreground mb-2 group-hover:opacity-70 transition-opacity">
                    {item.title}
                  </h2>
                  <p className="text-muted-foreground text-sm mb-4">
                    {item.excerpt}
                  </p>
                </div>
              </div>

              {/* Content */}
              <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                {item.content}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {item.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    #{tag.toLowerCase()}
                  </Badge>
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center gap-6 text-xs text-muted-foreground">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={() => toggleLike(item.id)}
                    className={`flex items-center gap-1 transition-colors ${
                      item.liked ? "text-red-500" : "group-hover:text-foreground"
                    }`}
                  >
                    <Heart className={`h-4 w-4 ${item.liked ? "fill-current" : ""}`} />
                    {item.likes.toLocaleString()}
                  </motion.button>
                  <button className="flex items-center gap-1 hover:text-foreground transition-colors">
                    <MessageCircle className="h-4 w-4" />
                    {item.comments.toLocaleString()}
                  </button>
                  <button className="flex items-center gap-1 hover:text-foreground transition-colors">
                    <Share2 className="h-4 w-4" />
                    Share
                  </button>
                </div>
                <Button size="sm" variant="outline" className="border-border">
                  Read More
                </Button>
              </div>
            </motion.div>
          ))
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 border border-border p-8"
          >
            <p className="text-muted-foreground">No updates found. Try adjusting your filters.</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
